import { withApiBaseUrl } from '../lib/apiBaseUrl'

export interface UserCashbackDisplaySettings {
  pageTitle: string
  pageSubtitle: string
  heroBadgeText: string
  withdrawalButtonText: string
  noticeText: string
  minWithdrawalAmount: number
  cashbackRateText: string
  promotionalMessage: string
  disabledTitle: string
  disabledMessage: string
  historyEmptyText: string
  showRateTable: boolean
  showExample: boolean
  promotionRoute: string
  promotionCard: UserCashbackPromotionCardSettings
}

export interface UserCashbackPromotionCardSettings {
  imageUrl: string
  imageAlt: string
  badgeText: string
  title: string
  description: string
  buttonText: string
}

export interface UserCashbackPromotionConfig {
  promotionType: 'cashback'
  enabled: boolean
  settings?: {
    display?: Partial<UserCashbackDisplaySettings>
  }
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface UserCashbackDisplayPromotionConfig {
  promotionType: 'cashback_display'
  enabled: boolean
  settings?: Partial<UserCashbackDisplaySettings>
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface UserPromotionConfigResponse<TConfig> {
  success: boolean
  data?: TConfig
  message?: string
}

export interface UserReferralDisplaySettings {
  pageTitle: string
  pageSubtitle: string
  withdrawButtonText: string
  shareSectionTitle: string
  linkLabel: string
  copyButtonText: string
  shareDescription: string
  disabledTitle: string
  disabledMessage: string
  historyEmptyText: string
  hideZeroCommissionSegments: boolean
  promotionRoute: string
  promotionCard: UserReferralPromotionCardSettings
}

export interface UserReferralPromotionCardSettings {
  imageUrl: string
  imageAlt: string
  badgeText: string
  title: string
  description: string
  buttonText: string
}

export interface UserReferralDisplayPromotionConfig {
  promotionType: 'referral_display'
  enabled: boolean
  settings?: Partial<UserReferralDisplaySettings>
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface UserManualReviewBonusPromotionCardSettings {
  imageUrl: string
  imageAlt: string
  badgeText: string
  title: string
  description: string
  buttonText: string
}

export interface UserManualReviewBonusDisplaySettings {
  pageTitle: string
  pageSubtitle: string
  contactSectionTitle: string
  contactSectionSubtitle: string
  requirements: string[]
  disabledTitle: string
  disabledMessage: string
  promotionCard: UserManualReviewBonusPromotionCardSettings
}

export interface UserBirthdayDisplayPromotionConfig {
  promotionType: 'birthday_display'
  enabled: boolean
  settings?: Partial<UserManualReviewBonusDisplaySettings>
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface UserFacebookShareDisplayPromotionConfig {
  promotionType: 'facebook_share_display'
  enabled: boolean
  settings?: Partial<UserManualReviewBonusDisplaySettings>
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface UserMonthlyRewardTabSettings {
  enabled?: boolean
  title?: string
}

export interface UserMonthlyRewardPromotionConfig {
  promotionType: 'monthly_reward'
  enabled: boolean
  settings?: {
    display?: Partial<UserMonthlyRewardDisplaySettings>
    tabs?: Partial<Record<'cashback' | 'loss' | 'refer' | 'rank', UserMonthlyRewardTabSettings>>
  }
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface UserMonthlyRewardDisplaySettings {
  pageTitle: string
  subtitle: string
  rowsPerTab: number
  promotionCard: UserMonthlyRewardPromotionCardSettings
}

export interface UserMonthlyRewardPromotionCardSettings {
  badgeText: string
  title: string
  description: string
  buttonText: string
  route: string
}

export interface UserPromotionHubShortcutSettings {
  enabled: boolean
  sortOrder: number
  badgeText: string
  title: string
  description: string
  buttonText: string
  route: string
}

export type UserPromotionHubCardActionType = 'none' | 'campaign' | 'route' | 'url'

export interface UserPromotionHubManualCard {
  id: string
  enabled: boolean
  sortOrder: number
  badgeText: string
  title: string
  description: string
  imageUrl: string
  imageAlt: string
  buttonText: string
  actionType: UserPromotionHubCardActionType
  actionValue: string
  openInNewTab: boolean
  startsAt: string | null
  endsAt: string | null
}

export interface UserPromotionHubSectionSettings {
  enabled: boolean
  sortOrder: number
}

export interface UserPromotionHubDisplaySettings {
  pageTitle: string
  pageSubtitle: string
  heroBadgeText: string
  warningTitle: string
  warningMessagePrimary: string
  warningMessageSecondary: string
  featuredSectionTitle: string
  featuredSectionSubtitle: string
  campaignsSectionTitle: string
  campaignsSectionSubtitle: string
  emptyCampaignText: string
  receiveButtonText: string
  showCampaigns: boolean
  disabledTitle: string
  disabledMessage: string
  sections: {
    warning: UserPromotionHubSectionSettings
    featuredPromotions: UserPromotionHubSectionSettings
    shortcuts: UserPromotionHubSectionSettings
    campaigns: UserPromotionHubSectionSettings
  }
  manualCards: UserPromotionHubManualCard[]
  shortcuts: {
    cashback: UserPromotionHubShortcutSettings
    monthlyReward: UserPromotionHubShortcutSettings
    referral: UserPromotionHubShortcutSettings
    birthday: UserPromotionHubShortcutSettings
    facebookShare: UserPromotionHubShortcutSettings
  }
}

export interface UserPromotionHubDisplayPromotionConfig {
  promotionType: 'promotion_hub_display'
  enabled: boolean
  settings?: Partial<UserPromotionHubDisplaySettings>
  updatedAt?: string | null
  updatedBy?: string | null
}

export async function getCashbackPromotionConfig(tenantId?: string): Promise<UserPromotionConfigResponse<UserCashbackPromotionConfig>> {
  const headers: Record<string, string> = {}
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId
  }

  const res = await fetch(withApiBaseUrl('/api/v1/user-auth/promotion-configs/cashback'), {
    method: 'GET',
    headers,
    credentials: 'include',
  })

  const data = await res.json().catch(() => ({ success: false, message: 'Invalid cashback config response' }))
  return data as UserPromotionConfigResponse<UserCashbackPromotionConfig>
}

export async function getCashbackDisplayPromotionConfig(tenantId?: string): Promise<UserPromotionConfigResponse<UserCashbackDisplayPromotionConfig>> {
  const headers: Record<string, string> = {}
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId
  }
  headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
  headers.Pragma = 'no-cache'

  const res = await fetch(withApiBaseUrl(`/api/v1/user-auth/promotion-configs/cashback_display?_t=${Date.now()}`), {
    method: 'GET',
    headers,
    credentials: 'include',
    cache: 'no-store',
  })

  const data = await res.json().catch(() => ({ success: false, message: 'Invalid cashback display config response' }))
  return data as UserPromotionConfigResponse<UserCashbackDisplayPromotionConfig>
}

export async function getReferralDisplayPromotionConfig(tenantId?: string): Promise<UserPromotionConfigResponse<UserReferralDisplayPromotionConfig>> {
  const headers: Record<string, string> = {}
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId
  }
  headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
  headers.Pragma = 'no-cache'

  const res = await fetch(withApiBaseUrl(`/api/v1/user-auth/promotion-configs/referral_display?_t=${Date.now()}`), {
    method: 'GET',
    headers,
    credentials: 'include',
    cache: 'no-store',
  })

  const data = await res.json().catch(() => ({ success: false, message: 'Invalid referral display config response' }))
  return data as UserPromotionConfigResponse<UserReferralDisplayPromotionConfig>
}

export async function getBirthdayDisplayPromotionConfig(tenantId?: string): Promise<UserPromotionConfigResponse<UserBirthdayDisplayPromotionConfig>> {
  const headers: Record<string, string> = {}
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId
  }
  headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
  headers.Pragma = 'no-cache'

  const res = await fetch(withApiBaseUrl(`/api/v1/user-auth/promotion-configs/birthday_display?_t=${Date.now()}`), {
    method: 'GET',
    headers,
    credentials: 'include',
    cache: 'no-store',
  })

  const data = await res.json().catch(() => ({ success: false, message: 'Invalid birthday display config response' }))
  return data as UserPromotionConfigResponse<UserBirthdayDisplayPromotionConfig>
}

export async function getFacebookShareDisplayPromotionConfig(tenantId?: string): Promise<UserPromotionConfigResponse<UserFacebookShareDisplayPromotionConfig>> {
  const headers: Record<string, string> = {}
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId
  }
  headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
  headers.Pragma = 'no-cache'

  const res = await fetch(withApiBaseUrl(`/api/v1/user-auth/promotion-configs/facebook_share_display?_t=${Date.now()}`), {
    method: 'GET',
    headers,
    credentials: 'include',
    cache: 'no-store',
  })

  const data = await res.json().catch(() => ({ success: false, message: 'Invalid facebook share display config response' }))
  return data as UserPromotionConfigResponse<UserFacebookShareDisplayPromotionConfig>
}

export async function getMonthlyRewardPromotionConfig(tenantId?: string): Promise<UserPromotionConfigResponse<UserMonthlyRewardPromotionConfig>> {
  const headers: Record<string, string> = {}
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId
  }
  headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
  headers.Pragma = 'no-cache'

  const res = await fetch(withApiBaseUrl(`/api/v1/user-auth/promotion-configs/monthly_reward?_t=${Date.now()}`), {
    method: 'GET',
    headers,
    credentials: 'include',
    cache: 'no-store',
  })

  const data = await res.json().catch(() => ({ success: false, message: 'Invalid monthly reward config response' }))
  return data as UserPromotionConfigResponse<UserMonthlyRewardPromotionConfig>
}

export async function getPromotionHubDisplayPromotionConfig(tenantId?: string): Promise<UserPromotionConfigResponse<UserPromotionHubDisplayPromotionConfig>> {
  const headers: Record<string, string> = {}
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId
  }
  headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
  headers.Pragma = 'no-cache'

  const res = await fetch(withApiBaseUrl(`/api/v1/user-auth/promotion-configs/promotion_hub_display?_t=${Date.now()}`), {
    method: 'GET',
    headers,
    credentials: 'include',
    cache: 'no-store',
  })

  const data = await res.json().catch(() => ({ success: false, message: 'Invalid promotion hub display config response' }))
  return data as UserPromotionConfigResponse<UserPromotionHubDisplayPromotionConfig>
}
